import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Alert } from 'antd';
import dayjs from 'dayjs';
import i18next from 'i18next';
import styled from 'styled-components';

import { APPLICATIONS, APPLICATIONS_ENG } from 'entities/application/constants';
import { useInvoices } from 'entities/invoice/model/useInvoices';
import { InvoiceCard } from 'entities/invoice/ui/InvoiceCard/InvoiceCard';
import { useInvoiceStatus } from 'features/navigation/model/useInvoiceStatus';
import {
	HeadingStyled,
	InvoicesAppBlockStyled,
	MyInvoicesHeaderStyled,
	TextStyled,
} from 'features/show-invoices/ui/MyInvoices.styles';
import { CenteredContainerStyled } from 'features/show-subscriptions/ui/MySubscription.styles';
import { ErrorImage } from 'shared/ui/ErrorImage';

import './MyInvoices.css';

const TitleStyled = styled(`h2`)`
	margin-top: 8px;
	margin-bottom: 10px;
	font-size: 20px;
`;

const InvoicesContainerStyled = styled.div`
	gap: 12px;
	display: flex;
	flex-direction: column;
`;

export const MyBills: FC = () => {
	const { t, i18n } = useTranslation();
	const invoiceStatus = useInvoiceStatus();
	const invoices = useInvoices();

	if (!invoices.length) {
		return (
			<div style={{ width: `100%` }}>
				<MyInvoicesHeaderStyled>{t(`Marketplace_My_Bills`)}</MyInvoicesHeaderStyled>
				<CenteredContainerStyled>
					<ErrorImage src='https://cdn.dodostatic.net/static/marketplace/no-invoices' />
					<HeadingStyled>{t(`Marketplace_No_Invoices_Heading`)}</HeadingStyled>
					<TextStyled>{t(`Marketplace_No_Invoices`)}</TextStyled>
				</CenteredContainerStyled>
			</div>
		);
	}

	const totalInvoiceAmount = invoices.reduce(
		(total, item) =>
			total +
			item.invoices
				.filter(n => n.status === 1 || n.status === 3)
				.reduce((subTotal, invoice) => subTotal + invoice.invoiceAmount, 0),
		0,
	);

	const allDates = invoices.flatMap(item =>
		item.invoices.filter(n => n.status === 1 || n.status === 3).map(invoice => new Date(invoice.invoicedAt)),
	);
	const earliestDate = Math.min(...allDates.map(date => date.getTime()));

	return (
		<div style={{ width: `100%` }}>
			<MyInvoicesHeaderStyled>{t(`Marketplace_My_Bills`)}</MyInvoicesHeaderStyled>
			{(invoiceStatus === 1 || invoiceStatus === 2) && (
				<Alert
					style={{ marginBottom: `36px`, marginTop: `8px` }}
					className='invoices-alert'
					message={
						dayjs(earliestDate).isBefore(dayjs())
							? t(`Marketplace_Pay_Invoices_Without_Date`)
							: t(`Marketplace_Invoices_Alert_Heading`, {
									date: dayjs(earliestDate).locale(i18n.language.slice(3)).format(`D MMMM`),
							  })
					}
					type='error'
					showIcon
					icon={<ExclamationCircleOutlined />}
					description={t(`Marketplace_Invoices_Alert_Text`, {
						sum: Number(totalInvoiceAmount.toFixed(2)),
					})}
				/>
			)}
			<InvoicesAppBlockStyled>
				{invoices.map(n => (
					<div key={n.applicationId}>
						<TitleStyled>
							{i18next.language === `ru-RU`
								? APPLICATIONS.find(app => app.id === n.applicationId.toUpperCase())?.name
								: APPLICATIONS_ENG.find(app => app.id === n.applicationId.toUpperCase())?.name}
						</TitleStyled>
						<InvoicesContainerStyled>
							{n.invoices.map(invoice => (
								<InvoiceCard invoice={invoice} key={invoice.id} />
							))}
						</InvoicesContainerStyled>
					</div>
				))}
			</InvoicesAppBlockStyled>
		</div>
	);
};
